/** Shell color roles derived from the supplied Core Elements Master Policy. */
import type { PaletteOptions, Theme } from '@mui/material/styles';
import { alpha, getContrastRatio } from '@mui/material/styles';
import type { ClientThemeOverrides } from '../utils/themeAugmentation';
import logo from '../../../themes/shell/shell.png';
import dashboardBannerImage from '../../../assets/shell-banner-1600x150.svg';
import { shellColors as c, createShellUi } from './shell.colors';
import type { PaletteRole } from '../../utils/paletteRoleColors';

const shellChartSeries = {
  // p. 51: "Use tints for data visualisations where they will help
  // information to be presented more clearly." Preserve series identity/order.
  light: {
    // Yellow, Red, Sunrise, Earth, Sunset, Stone (P1–P6).
    periods: [c.yellow, c.red, c.sunrise300, c.earth700, c.sunset300, c.stone300],
    meters: [c.yellow, c.red, c.sunrise300, c.earth700, c.sunset300, c.stone300, c.sunset500, c.yellow300, c.earth400, c.sand100],
    trendLine: c.grey700,
  },
  dark: {
    periods: [c.yellow, c.red400, c.sunrise300, c.earth400, c.sunset200, c.stone300],
    meters: [c.yellow, c.red400, c.sunrise300, c.earth400, c.sunset200, c.stone300, c.sunset300, c.yellow100, c.earth300, c.sand100],
    trendLine: c.grey300,
  },
} as const;


function palette(mode: 'light' | 'dark'): PaletteOptions {
  const dark = mode === 'dark';
  const ui = createShellUi(mode);
  const paper = dark ? c.grey800 : c.white;
  const text = dark ? c.grey100 : c.grey700;
  const secondaryText = dark ? c.grey300 : c.grey600;
  const divider = dark ? c.grey600 : c.grey100;
  // p. 81: "Only use colours from the Shell palette."
  // Override extensions too: createThemeConfig deep-merges the Atlas palette.
  const primary = { main: c.yellow, light: c.yellow100, dark: c.yellow300,
    contrastText: c.grey700, soft: ui.selectedBackground };
  const background = { default: dark ? c.grey900 : c.grey50, paper, elevated: paper };
  const action = { active: ui.icon, hover: alpha(ui.icon, 0.08), selected: ui.selectedBackground,
    disabled: c.grey400, disabledBackground: alpha(ui.icon, 0.12), focus: alpha(ui.focus, 0.12),
    primary: c.yellow, primaryHover: c.yellow100, primarySoft: ui.selectedBackground,
    primaryContrastText: c.grey700 };
  return {
    common: { black: c.grey900, white: c.white },
    grey: { 50: c.grey50, 100: c.grey100, 200: c.grey200, 300: c.grey300, 400: c.grey400,
      500: c.grey500, 600: c.grey600, 700: c.grey700, 800: c.grey800, 900: c.grey900,
      A100: c.grey50, A200: c.grey100, A400: c.grey300, A700: c.grey600 },
    primary,
    secondary: { main: dark ? c.grey300 : c.grey700, light: dark ? c.grey100 : c.grey600,
      dark: dark ? c.grey400 : c.grey800, contrastText: dark ? c.grey900 : c.white },
    success: { main: dark ? c.forest300 : c.forest500, light: dark ? c.forest200 : c.forest300,
      dark: dark ? c.forest500 : c.forest700, contrastText: dark ? c.grey900 : c.white },
    warning: { main: dark ? c.sunrise200 : c.sunrise300, light: c.sunrise200,
      dark: c.sunrise600, contrastText: dark ? c.grey700 : c.grey900 },
    info: { main: dark ? c.ocean200 : c.ocean600, light: dark ? c.ocean100 : c.ocean200,
      dark: c.ocean700, contrastText: dark ? c.grey700 : c.white },
    error: { main: dark ? c.red300 : c.red, light: c.red300,
      dark: c.red600, contrastText: dark ? c.grey900 : c.white },
    background,
    text: { primary: text, secondary: secondaryText, disabled: c.grey400 },
    divider,
    action,
    atlas: {
      ui, accent: c.yellow,
      status: { active: ui.badgeColors.success.subtle.color, activeBg: ui.badgeColors.success.subtle.backgroundColor,
        expiring: ui.badgeColors.warning.subtle.color, expiringBg: ui.badgeColors.warning.subtle.backgroundColor,
        historical: ui.badgeColors.neutral.subtle.color, historicalBg: ui.badgeColors.neutral.subtle.backgroundColor },
      surface: { card: paper, cardBorder: divider, subtle: dark ? c.grey900 : c.grey50 },
      table: { headerBg: dark ? c.grey900 : c.grey50, headerText: text, rowAlt: paper,
        rowHover: alpha(ui.icon, 0.08), border: divider },
      filterChips: {
        commodity: { foreground: ui.icon, background: ui.iconBackground },
        titular: { foreground: ui.icon, background: ui.iconBackground },
        cups: { foreground: ui.icon, background: ui.iconBackground },
        contrato: { foreground: ui.selectedForeground, background: ui.selectedBackground },
        period: { foreground: ui.icon, background: ui.iconBackground },
      },
      chart: { ...shellChartSeries[mode], gas: c.yellow, electricity: dark ? c.red400 : c.red,
        grid: dark ? c.grey300 : c.grey500, cursor: ui.selectedBackground },
      navigation: { background: paper, secondaryBackground: paper,
        hoverBackground: alpha(ui.icon, 0.08), text, textInactive: secondaryText,
        activeIndicator: dark ? c.red400 : c.red, ghostButtonBorder: divider },
    },
  };
}

function containedHover(theme: Theme, role: 'success' | 'warning' | 'info' | 'error') {
  const color = theme.palette[role];
  return [color.dark, color.light, color.main].find(background =>
    getContrastRatio(color.contrastText, background) >= 4.5)!;
}

function semanticButton(theme: Theme, role: 'success' | 'warning' | 'info' | 'error') {
  return { color: theme.palette.atlas!.ui!.badgeColors[role].subtle.color };
}

function semanticOutline(theme: Theme, role: 'success' | 'warning' | 'info' | 'error') {
  const { color } = semanticButton(theme, role);
  return { color, borderColor: color, '&:hover': { borderColor: color } };
}

export const shellThemeOverrides: ClientThemeOverrides = {
  clientId: 'shell', logo, clientName: 'Shell', hideHeaderClientName: true, dashboardBannerImage,
  palette: { light: palette('light'), dark: palette('dark') },
  // Preserve existing type sizes and weights; headings use the normal text color.
  typography: {
    h1: { fontSize: '1.5rem', fontWeight: 500 }, h2: { fontSize: '1.2rem', fontWeight: 400 },
    h5: { fontSize: '1.1rem', fontWeight: 400 }, h6: { fontSize: '0.9rem', fontWeight: 400 },
    subtitle1: { fontSize: '0.9rem', fontWeight: 600 }, subtitle2: { fontSize: '0.9rem', fontWeight: 400 },
  },
  components: {
    // p. 81, Don'ts: "Use gradients or metallics."
    // Material's dark elevation gradient changes the actual contrast surface.
    // Keep content on the approved paper swatch; preserve header/menu/drawer appearance.
    MuiPaper: { styleOverrides: { root: {
      '&:not(.MuiAppBar-root):not(.MuiDrawer-paper):not(.MuiMenu-paper)': { backgroundImage: 'none' },
    } } },
    // Functional indicators need contrast; yellow remains the brand/action fill.
    MuiCircularProgress: { styleOverrides: { colorPrimary: ({ theme }) => ({ color: theme.palette.atlas!.ui!.control }) } },
    MuiLinearProgress: { styleOverrides: {
      colorPrimary: ({ theme }) => ({ backgroundColor: theme.palette.atlas!.ui!.iconBackground }),
      barColorPrimary: ({ theme }) => ({ backgroundColor: theme.palette.atlas!.ui!.control }),
    } },
    MuiTypography: { styleOverrides: {
      h1: { fontSize: '1.5rem', fontWeight: 500 }, h2: { fontSize: '1.2rem', fontWeight: 400 },
      h5: ({ theme }) => ({ fontSize: '1.1rem', fontWeight: 400, color: theme.palette.text.primary }),
      h6: ({ theme }) => ({ fontSize: '0.9rem', fontWeight: 400, color: theme.palette.text.primary }),
      subtitle1: { fontSize: '0.9rem', fontWeight: 600 }, subtitle2: { fontSize: '0.9rem', fontWeight: 400 },
    } },
    MuiButton: { styleOverrides: {
      root: ({ theme }) => ({ borderRadius: theme.shape.borderRadius, textTransform: 'none', fontWeight: 600,
        '&:focus-visible': { outline: `2px solid ${theme.palette.atlas!.ui!.focus}`, outlineOffset: 2 } }),
      // p. 45: "Don't use darker tint where lighter tint provides sufficient contrast."
      // Grey 700 meets 4.5:1 on Yellow 200 and Yellow 100 (hover), not Yellow 300.
      containedPrimary: { backgroundColor: c.yellow, color: c.grey700,
        '&:hover': { backgroundColor: c.yellow100 } },
      outlinedPrimary: ({ theme }) => ({ color: theme.palette.atlas!.ui!.icon,
        borderColor: theme.palette.atlas!.ui!.control,
        '&:hover': { borderColor: theme.palette.atlas!.ui!.control, backgroundColor: theme.palette.action.hover } }),
      textPrimary: ({ theme }) => ({ color: theme.palette.atlas!.ui!.link,
        '&:hover': { backgroundColor: theme.palette.action.hover } }),
      containedSuccess: ({ theme }) => ({ '&:hover': { backgroundColor: containedHover(theme, 'success') } }),
      containedWarning: ({ theme }) => ({ '&:hover': { backgroundColor: containedHover(theme, 'warning') } }),
      containedInfo: ({ theme }) => ({ '&:hover': { backgroundColor: containedHover(theme, 'info') } }),
      containedError: ({ theme }) => ({ '&:hover': { backgroundColor: containedHover(theme, 'error') } }),
      textSuccess: ({ theme }) => semanticButton(theme, 'success'),
      textWarning: ({ theme }) => semanticButton(theme, 'warning'),
      textInfo: ({ theme }) => semanticButton(theme, 'info'),
      textError: ({ theme }) => semanticButton(theme, 'error'),
      outlinedSuccess: ({ theme }) => semanticOutline(theme, 'success'),
      outlinedWarning: ({ theme }) => semanticOutline(theme, 'warning'),
      outlinedInfo: ({ theme }) => semanticOutline(theme, 'info'),
      outlinedError: ({ theme }) => semanticOutline(theme, 'error'),
    } },
    MuiIconButton: { styleOverrides: { colorPrimary: ({ theme }) => ({ color: theme.palette.atlas!.ui!.icon }),
      root: ({ theme, ownerState }) => ({ color: ownerState.color === 'inherit' ? undefined : theme.palette.atlas!.ui!.icon,
        '&:focus-visible': { outline: `2px solid ${theme.palette.atlas!.ui!.focus}`, outlineOffset: 2 } }) } },
    MuiSvgIcon: { styleOverrides: { root: ({ theme, ownerState }) => ({ color:
      ['primary', 'secondary', 'success', 'warning', 'info', 'error'].includes(ownerState.color!)
        ? theme.palette.atlas!.ui!.icon : undefined }) } },
    MuiIcon: { styleOverrides: { root: ({ theme, ownerState }) => ({ color:
      ['primary', 'secondary', 'success', 'warning', 'info', 'error'].includes(ownerState.color!)
        ? theme.palette.atlas!.ui!.icon : undefined }) } },
    MuiChip: { styleOverrides: { root: ({ theme, ownerState }) => {
      const ui = theme.palette.atlas!.ui!;
      const colors = ui.badgeColors[ownerState.color as PaletteRole];
      const filled = colors?.solid ?? { color: ui.icon, backgroundColor: ui.iconBackground };
      return {
        ...(ownerState.variant === 'outlined'
          ? { color: ownerState.color === 'primary' ? ui.icon : colors?.subtle.color }
          : { ...filled, '&:hover, &.Mui-focusVisible': { backgroundColor: filled.backgroundColor } }),
        '& .MuiChip-icon, & .MuiChip-deleteIcon, & .MuiChip-deleteIcon:hover': { color: 'inherit' },
      };
    } } },
    MuiAlert: { styleOverrides: { root: ({ theme, ownerState }) => {
      const pair = theme.palette.atlas!.ui!.badgeColors[ownerState.severity ?? 'success'][ownerState.variant === 'filled' ? 'solid' : 'subtle'];
      const icon = [c.grey700, c.grey100, c.grey900, c.white].find(color =>
        getContrastRatio(color, pair.backgroundColor) >= 3)!;
      return { color: pair.color, backgroundColor: pair.backgroundColor,
        '& .MuiAlert-icon': { color: icon } };
    } } },
    MuiLink: { styleOverrides: { root: ({ theme, ownerState }) => ownerState.color === 'primary'
      ? { color: theme.palette.atlas!.ui!.link, '&:hover': { color: theme.palette.atlas!.ui!.linkHover } } : {} } },
    MuiCheckbox: { styleOverrides: { root: ({ theme, ownerState }) => ownerState.color === 'primary'
      ? { '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: theme.palette.atlas!.ui!.control },
        '&.Mui-disabled': { color: theme.palette.action.disabled },
        '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.atlas!.ui!.focus}`, outlineOffset: 2 } } : {} } },
    MuiRadio: { styleOverrides: { root: ({ theme, ownerState }) => ownerState.color === 'primary'
      ? { '&.Mui-checked': { color: theme.palette.atlas!.ui!.control },
        '&.Mui-disabled': { color: theme.palette.action.disabled },
        '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.atlas!.ui!.focus}`, outlineOffset: 2 } } : {} } },
    MuiSwitch: { styleOverrides: { switchBase: ({ theme }) => ({ '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.atlas!.ui!.focus}`, outlineOffset: 2 }, '&.Mui-checked': { color: theme.palette.atlas!.ui!.control,
      '& + .MuiSwitch-track': { backgroundColor: theme.palette.atlas!.ui!.control } },
      '&.Mui-checked.Mui-disabled': { color: theme.palette.action.disabled,
        '& + .MuiSwitch-track': { backgroundColor: theme.palette.action.disabled } } }) } },
    MuiSlider: { styleOverrides: { root: ({ theme, ownerState }) => ownerState.color === 'primary' ? { color: theme.palette.atlas!.ui!.control } : {} } },
    MuiOutlinedInput: { styleOverrides: { root: ({ theme }) => ({ '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.atlas!.ui!.control } }) } },
    MuiInputLabel: { styleOverrides: { root: ({ theme }) => ({ '&.Mui-focused': { color: theme.palette.atlas!.ui!.link } }) } },
    MuiTabs: { styleOverrides: { indicator: ({ theme }) => ({ backgroundColor: theme.palette.atlas!.ui!.selectedBorder }) } },
    MuiTab: { styleOverrides: { root: ({ theme }) => ({ '&.Mui-selected': { color: theme.palette.atlas!.ui!.selectedForeground, backgroundColor: theme.palette.atlas!.ui!.selectedBackground } }) } },
    MuiPaginationItem: { styleOverrides: { root: ({ theme }) => ({ '&.Mui-selected': { backgroundColor: theme.palette.atlas!.ui!.selectedBackground,
      color: theme.palette.atlas!.ui!.selectedForeground, border: `1px solid ${theme.palette.atlas!.ui!.selectedBorder}` },
      '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.atlas!.ui!.focus}`, outlineOffset: 2 } }) } },
    MuiToggleButton: { styleOverrides: { root: ({ theme }) => ({ '&.Mui-selected': { color: theme.palette.atlas!.ui!.selectedForeground, backgroundColor: theme.palette.atlas!.ui!.selectedBackground } }) } },
  },
  dataGrid: { highlightedCell: { backgroundColor: c.yellow, textColor: c.grey700 } },
};
